import * as React from 'react';

import { DispensaryContext } from '../dispensary/DispensaryProvider';
import { useSelector, useDispatch } from 'react-redux';
import { navigateTop } from '../routing/router-utils';
import { addToCart, setDispensary } from '../actions/cartActions';
import { trackEvent } from '../analytics/tracking';
import Header, { HeaderSize } from '../styleguide/Header';
import MediumButton from '../styleguide/MediumButton';
import AddedToCartCheck from '../products/AddedToCartCheck';
import DispensaryProduct from '../products/DispensaryProduct';
import DisplayModal from '../common/DisplayModal';
import SwitchDispensaryWarning from '../common/SwitchDispensaryWarning';
import styles from './DispensarySection.module.css';

import PropTypes from 'prop-types';

/**
 * This component will display a grid of category-specific products for a dispensary 
 * 
 * @param {string} dispensaryId - The dispensary Id
 * @param {object} dispensaryMap - Dispensary info (name, etc.) by Id
 * @param {string} productCategoryName - The category name
 * @param {number} categoryCount - The total number of category products
 * @param {object} searchResultProducts - The category products (subset for display)
 * @param {function} addToCart - for add-to-cart click
 * @param {function} setDispensary - to set dispensary on add-to-cart click 
 */
const DispensarySection = ({
  dispensaryId,
  distanceMap,
  productCategoryName,
  categoryCount=0,
  searchResultProducts=[]
}) => {

  const dispensaryMap = useSelector(state => state.dispensaryMap);
  const cartDispensaryId = useSelector(state => state.dispensaryId);
  const cartItems = useSelector(state => state.addedItems);

  const { setDispensaryId } = React.useContext(DispensaryContext);
  const dispatch = useDispatch();

  const [showAddedCheck, setShowAddedCheck] = React.useState();

  const [showCartWarning, setShowCartWarning] = React.useState();
  const [cartWarningItem, setCartWarningItem] = React.useState({});
  const [cartWarningQuantity, setCartWarningQuantity] = React.useState(1);  

  const handleAddToCart = (productId, productObj, quantity) => {
    trackEvent('shop_by_product_add_to_cart', productCategoryName, productId);
    // Handle warning on cart dispensary change
    if (cartItems.length > 0 && dispensaryId !== cartDispensaryId) {
      setCartWarningItem(productObj);
      setCartWarningQuantity(quantity);
      setShowCartWarning(true);
    } else {
      setShowAddedCheck(true);
      setDispensaryId(dispensaryId); 
      dispatch(setDispensary(dispensaryId)); 
      dispatch(addToCart(productId, productObj, quantity));
    }
  };

  const [dispensaryName, setDispensaryName] = React.useState();
  const [dispensaryid, setLocalDispensaryId] = React.useState();
  const [dispensaryLocation, setDispensaryLocation] = React.useState();
  

  React.useEffect(() => {
    if (!dispensaryName && dispensaryMap.get(dispensaryId)) {
      const dispensary = dispensaryMap.get(dispensaryId);
      setDispensaryName(dispensary.name);
      setLocalDispensaryId(dispensary.id);
      setDispensaryLocation(dispensary.location);
    }  
  }, [dispensaryId, dispensaryMap, dispensaryName]);
  
  const dispensaryClick = () => {
    navigateTop(`/dispensary/${dispensaryId}/${productCategoryName}`, { disableScroll:true });
  };

  return ( searchResultProducts.length && dispensaryName
    ? <>
        <div className={styles.sectionHeader}>
          <Header size={HeaderSize.Small}>
            <span className={styles.dispensaryName}>{dispensaryName}</span>
            <span className={styles.locationInfo}>
              {dispensaryLocation?.city}
              {' '}&middot;&nbsp;{distanceMap.get(dispensaryid)}
            </span>
          </Header>  
        </div>
        { searchResultProducts.map(product => {
          const productDispensary =  dispensaryMap.get(product.dispensary.id);
          const dispensaryName = productDispensary ? productDispensary.name : '';
          return <DispensaryProduct 
                    key={`${dispensaryId}:${product.id}`} 
                    Id={product.id}
                    {...product.display_info}
                    grams={product.grams}
                    leaf_logix_pricing_tier={product.leaf_logix_pricing_tier}
                    dispensaryId={dispensaryId}
                    dispensaryName={dispensaryName}
                    handleClick={(quantity) => { 
                      handleAddToCart(product.id, product, quantity); 
                    }} />
        })}
        
        <div className={styles.buttonWrap}>
          <MediumButton isCentered text={`Shop ${dispensaryName}`} handleClick={dispensaryClick} />
        </div>
        
        { showAddedCheck && 
          <AddedToCartCheck
            key={`chk${dispensaryId}`} 
            closeFn={() => { 
              setShowAddedCheck(false); 
              navigateTop(`/dispensary/${dispensaryId}/${productCategoryName}`, { disableScroll:true });
            }} 
            closeMs={2000}
            addlMessage={['Switching to the ', <b>{dispensaryName}</b>, ' dispensary']}  
          />
        }
        { showCartWarning &&
          <DisplayModal title="Start New Order?" closeFn={() => setShowCartWarning(false)}>
            <SwitchDispensaryWarning 
              itemDispensaryId={dispensaryId}
              handleAddToCart={() => {
                setDispensaryId(dispensaryId); 
                dispatch(setDispensary(cartWarningItem.dispensary.id));
                dispatch(addToCart(cartWarningItem.Id, cartWarningItem, cartWarningQuantity));
                setShowCartWarning(false);
                navigateTop(`/dispensary/${dispensaryId}/${productCategoryName}`);
              }} 
              handleClose={() => setShowCartWarning(false)}  
            />
          </DisplayModal>
        }
      </>
    : null
  );
}

DispensarySection.propTypes = {
  dispensaryId: PropTypes.string.isRequired,
  distanceMap: PropTypes.instanceOf(Map).isRequired,
  productCategoryName: PropTypes.string.isRequired,
  categoryCount: PropTypes.number.isRequired,
  searchResultProducts: PropTypes.array.isRequired
};

export default DispensarySection;
